import React, { useEffect, useState } from "react";
import type { Product } from "@/types";
import { useI18n } from "@/i18n";
import { useAppContext } from "@/state/AppContext";
import { ApiError, submitProductInquiry, type InquiryPurpose } from "@/lib/api";
import { haptic } from "@/lib/telegram";
import { MainAction } from "./MainAction";
import { useDesktop } from "@/lib/useDesktop";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";

const PURPOSES: InquiryPurpose[] = ["home", "office", "cafe"];

function PurposeIcon({ purpose }: { purpose: InquiryPurpose }) {
  const common = { width: 24, height: 24, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (purpose === "home") {
    return (
      <svg {...common}>
        <path d="M4 11 12 4l8 7" />
        <path d="M6 9.5V20h12V9.5" />
        <path d="M10 20v-5h4v5" />
      </svg>
    );
  }
  if (purpose === "office") {
    return (
      <svg {...common}>
        <rect x="5" y="3" width="14" height="18" rx="1.5" />
        <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 9h11v5a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5V9Z" />
      <path d="M16 10h1.5a2.5 2.5 0 0 1 0 5H16" />
      <path d="M8 3v3M11 3v3" />
    </svg>
  );
}

/** Keep only characters a phone number can contain; no auto-formatting that fights backspace. */
function sanitizePhone(raw: string): string {
  return raw.replace(/[^\d+\s()-]/g, "").slice(0, 20);
}

/**
 * Two different request forms, each asking only what makes sense for it:
 *  - "equipment": under-order goods — who it's for (home/office/café) so the manager can pick a setup;
 *  - "restock":   an out-of-stock product — just contacts and how many, "tell me when it's back".
 */
export function InquirySheet({
  product,
  open,
  onClose,
  mode = "equipment",
}: {
  product: Product;
  open: boolean;
  onClose: () => void;
  mode?: "equipment" | "restock";
}) {
  const { t, locale } = useI18n();
  const desktop = useDesktop();
  const isRestock = mode === "restock";
  const k = (key: string) => (isRestock ? `restock.${key}` : `inquiry.${key}`);
  const [quantity, setQuantity] = useState("1");
  const { user } = useAppContext();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [purpose, setPurpose] = useState<InquiryPurpose | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setServerError(null);
    setShowErrors(false);
    setFullName((prev) => prev || user?.firstName || "");
  }, [open, user?.firstName]);

  const phoneDigits = phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 9;
  const nameValid = fullName.trim().length >= 2;
  const formValid = phoneValid && nameValid && (isRestock || purpose !== null);

  const handleSubmit = async () => {
    if (sent) {
      onClose();
      return;
    }
    if (!formValid || (!isRestock && !purpose)) {
      setShowErrors(true);
      haptic("error");
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await submitProductInquiry({
        productId: product.id,
        fullName: fullName.trim(),
        phone: phone.trim(),
        purpose: isRestock ? "restock" : purpose!,
        comment: isRestock ? `Нужно: ${quantity || "1"} шт.` : undefined,
      });
      haptic("success");
      setSent(true);
    } catch (err) {
      haptic("error");
      setServerError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open || !desktop) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, desktop, onClose]);

  if (!open) return null;

  if (desktop) {
    const price = product.variants[0]?.priceUzs ?? 0;
    const perks = [t("desktop.inqPerkPrice"), t("desktop.inqPerkHours"), product.category === "coffee" ? t("desktop.inqPerkFresh") : t("desktop.inqPerkSetup")];
    return (
      <div className="sheet-overlay dq-overlay" onClick={onClose}>
        <div className="dq" role="dialog" aria-modal="true" aria-label={t(k("title"))} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="dq__x" onClick={onClose} aria-label={t("inquiry.close")}>×</button>
          <aside className="dq__aside">
            <div className="dq__photo">{product.images[0] && <img src={mediaUrl(product.images[0], 800)} alt={product.name} />}</div>
            {product.brand && <div className="dk-eyebrow">{product.brand}</div>}
            <h3 className="dk-serif">{product.name}</h3>
            {product.shortDescription && <p>{product.shortDescription}</p>}
            {price > 0 && <div className="dq__price">{formatUzs(price, locale)}</div>}
            <ul className="dq__perks">
              {perks.map((x) => (
                <li key={x}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  {x}
                </li>
              ))}
            </ul>
          </aside>
          <section className="dq__main">
            {sent ? (
              <div className="dq__done">
                <svg className="ddn-check" width="76" height="76" viewBox="0 0 84 84" aria-hidden="true"><circle cx="42" cy="42" r="38" /><path d="M26 43l11 11 21-23" /></svg>
                <h2 className="dk-serif">{t(k("sentTitle"))}</h2>
                <p>{t(k("sentBody"))}</p>
                <button type="button" className="dk-btn" onClick={onClose}>{t("inquiry.close")}</button>
              </div>
            ) : (
              <form
                className="dq__form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSubmit();
                }}
              >
                <h2 className="dk-serif">{t(k("title"))}</h2>
                <p className="dq__intro">{t(k("intro"))}</p>
                <div className="dq__row">
                  <label className="dq__field">
                    <span>{t("inquiry.fullName")}</span>
                    <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t("inquiry.fullNamePlaceholder")} autoComplete="name" aria-invalid={showErrors && !nameValid} />
                  </label>
                  <label className="dq__field">
                    <span>{t("inquiry.phone")}</span>
                    <input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(sanitizePhone(e.target.value))} placeholder="+998 90 123 45 67" aria-invalid={showErrors && !phoneValid} />
                    {showErrors && !phoneValid && <em>{t("inquiry.phoneInvalid")}</em>}
                  </label>
                </div>
                {isRestock ? (
                  <label className="dq__field dq__qty">
                    <span>{t("restock.quantity")}</span>
                    <input inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, "").slice(0, 3))} />
                  </label>
                ) : (
                  <div className="dq__field">
                    <span>{t("inquiry.purposeLabel")}</span>
                    <div className="dq__purposes" role="radiogroup" aria-label={t("inquiry.purposeLabel")}>
                      {PURPOSES.map((p) => (
                        <button key={p} type="button" role="radio" aria-checked={purpose === p} data-active={purpose === p} onClick={() => setPurpose(p)}>
                          <PurposeIcon purpose={p} />
                          {t(`inquiry.purpose.${p}`)}
                        </button>
                      ))}
                    </div>
                    {showErrors && purpose === null && <em>{t("inquiry.purposeRequired")}</em>}
                  </div>
                )}
                {serverError && <div className="form-error-banner">{serverError}</div>}
                <button type="submit" className="dk-btn dq__submit" disabled={submitting}>{submitting ? "…" : t(k("submit"))}</button>
                <p className="dq__note">{t("desktop.inqNote")}</p>
              </form>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={t(k("title"))} onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />

        {sent ? (
          <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
            <div className="sheet__success-icon">✓</div>
            <h2 className="sheet__title">{t(k("sentTitle"))}</h2>
            <p className="sheet__text">{t(k("sentBody"))}</p>
          </div>
        ) : (
          <>
            <h2 className="sheet__title">{t(k("title"))}</h2>
            <p className="sheet__text">
              <strong>{product.name}</strong>
              <br />
              {t(k("intro"))}
            </p>

            <div className="field">
              <label htmlFor="inq-name">{t("inquiry.fullName")}</label>
              <input
                id="inq-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t("inquiry.fullNamePlaceholder")}
                autoComplete="name"
                aria-invalid={showErrors && !nameValid}
              />
            </div>

            <div className="field">
              <label htmlFor="inq-phone">{t("inquiry.phone")}</label>
              <input
                id="inq-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                placeholder="+998 90 123 45 67"
                aria-invalid={showErrors && !phoneValid}
              />
              {showErrors && !phoneValid && <span className="field__error">{t("inquiry.phoneInvalid")}</span>}
            </div>

            {isRestock ? (
              <div className="field">
                <label htmlFor="inq-qty">{t("restock.quantity")}</label>
                <input id="inq-qty" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, "").slice(0, 3))} />
              </div>
            ) : (
            <>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>{t("inquiry.purposeLabel")}</label>
            </div>
            <div className="purpose-options" role="radiogroup" aria-label={t("inquiry.purposeLabel")}>
              {PURPOSES.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={purpose === p}
                  className="purpose-option"
                  data-active={purpose === p}
                  onClick={() => {
                    haptic("selection");
                    setPurpose(p);
                  }}
                >
                  <PurposeIcon purpose={p} />
                  {t(`inquiry.purpose.${p}`)}
                </button>
              ))}
            </div>
            {showErrors && purpose === null && <span className="field__error">{t("inquiry.purposeRequired")}</span>}
            </>
            )}
            {serverError && <div className="field__error" style={{ marginTop: 8 }}>{serverError}</div>}
          </>
        )}

        <MainAction text={sent ? t("inquiry.close") : t(k("submit"))} onClick={handleSubmit} loading={submitting} hidden={!open} />
      </div>
    </div>
  );
}
